/**
 * 3D ビュー（CesiumJS 1.145.0、npm から版固定で同梱）。サンプル（WebUI-Sample/3D-Portal-Google3D.html）の
 * 「10. 3D」「11. 3D描画」と同じ動き: 全国表示（県境・高速道路・現場ピン・ラベル）、現場表示（地形・計画面・
 * 構造物・重機）、視点、レイヤー、Google 実写 3D（Photorealistic 3D Tiles）と簡易地形モード。
 * API キーはリポジトリに置かない。利用者が画面で入れ、選んだときだけこのブラウザに保存する（ADR-0004）。
 */
import * as C from 'cesium';
import { clamp } from '../../core/math';
import { escapeHtml as esc, safeColor } from '../../core/text';
import type { MapLines, PrefShape } from '../../data/types';
import { B, sampleContext } from '../../model';
import type { Site } from '../../model';
import type { SiteView, Store } from '../../app/store';
import {
  computeEquip,
  contextMesh,
  createSiteDraw,
  designLines,
  dynamicItems,
  hex,
  sceneItems,
  siteTop,
  updateGround,
} from '../geometry';
import type { Equip, SiteDraw } from '../geometry';
import { NATION_LINE_STYLE, nationLineSpecs } from './nationLines';
import type { NationLineKind } from './nationLines';
import { BoxPool, P_YUP, boxesPrimitive, gridGeometry, meshPrimitive } from './primitives';

export type NetStatus = 'init' | 'loading' | 'google' | 'simple' | 'error' | 'nolib';
export const NET_TEXT: Readonly<Record<NetStatus, string>> = {
  init: '3D 準備中',
  loading: 'Google 3D 接続中…',
  google: 'Google 実写3D（オンライン）',
  simple: '簡易地形モード',
  error: 'Google 3D 未接続',
  nolib: '3D 表示不可',
};

/** レイヤー（元仕様 §3.7。初期値はサンプルの state.layers）。 */
// 型の別名にしておくと、画面側の Record<string, boolean> に渡せる
export type Layers3D = {
  heat: boolean;
  design: boolean;
  equip: boolean;
  route: boolean;
  crane: boolean;
  labels: boolean;
  roads: boolean;
  g3d: boolean;
};
export const INITIAL_LAYERS: Readonly<Layers3D> = {
  heat: false,
  design: false,
  equip: true,
  route: true,
  crane: false,
  labels: true,
  roads: true,
  g3d: true,
};

const HOME = { lon: 136.6, lat: 35.2, heading: 0, pitch: -1.12, range: 3.3e6 };
/** ジオイド高（楕円体高 − 標高）の初期概算。Google 地表との比較で現場ごとに補正する。 */
const GEOID0: Readonly<Record<string, number>> = {
  北海道: 31,
  宮城県: 38,
  新潟県: 38,
  東京都: 37,
  神奈川県: 37,
  埼玉県: 37,
  愛知県: 38,
  長野県: 40,
  大阪府: 37,
  兵庫県: 37,
  広島県: 35,
  愛媛県: 35,
  福岡県: 33,
  熊本県: 33,
  沖縄県: 31,
};

/**
 * 【不変条件】Polyline に渡す Material は 1 本ごとに新しく作る。Polyline どうしで共有してはいけない。
 *
 * 理由: PolylineCollection を destroy すると、中の各 Polyline が自分の `_material.destroy()` を呼ぶ
 * （PolylineCollection.destroy → destroyPolylines → Polyline._destroy）。Material を共有していると
 * 2 本目以降が「破棄済みの Material を破棄」となり DeveloperError（This object was destroyed, i.e.,
 * destroy() was called.）を投げ、Cesium は「An error occurred while rendering. Rendering has
 * stopped.」で描画を恒久停止する（Material は destroy のとき全メソッドを throwOnDestroyed に
 * 置き換える）。サンプル（CDN のリリースビルド）は debug 表明が除かれていて表面化しないが、同じ
 * 潜在バグである。よって Material は必ずこの関数を通して作る（呼ぶたびに新しい実体を返す）。
 */
function colorMaterial(color: C.Color): C.Material {
  return C.Material.fromType('Color', { color });
}

/** 全国表示の線の色。種別ごとの値はサンプルと同じ（`nationLines.ts`）。 */
function nationLineColor(kind: NationLineKind): C.Color {
  const s = NATION_LINE_STYLE[kind];
  return C.Color.fromCssColorString(s.color).withAlpha(s.alpha);
}

/** [経度, 緯度] の列を、Cesium の「経度・緯度・高さ」の配列に変換する。 */
function degreesArrayHeights(
  positions: readonly (readonly [number, number])[],
  height: number,
): C.Cartesian3[] {
  const a: number[] = [];
  for (const [lon, lat] of positions) a.push(lon, lat, height);
  return C.Cartesian3.fromDegreesArrayHeights(a);
}

interface SiteGeo {
  geo: number;
  geoCal: boolean;
  enu?: C.Matrix4;
  fr?: C.Matrix4;
  frGeo?: number;
  pin?: C.Cartesian3;
}
interface SiteView3D {
  yaw: number;
  pitch: number;
  dist: number;
  ty: number;
}

export interface SceneHooks {
  /** 状態表示（ヘッダーの .net）を変える。 */
  setStatus(kind: NetStatus, msg?: string): void;
  toast(html: string, ms?: number): void;
  /** Google への接続に失敗したとき（原因の診断を付けてキーの画面を開く）。 */
  onConnectFailed(diag: Diagnosis, key: string): void;
  /** 地面のクリックで都道府県を選ぶ／外す。 */
  pickPref(name: string): void;
  /** Google 3D の表示が変わったとき（台帳・サマリの文言が変わる）。 */
  onGoogleChange(): void;
}

export interface Diagnosis {
  title: string;
  steps: string[];
  raw: string;
}

export class CesiumScene {
  readonly viewer: C.Viewer;
  private readonly scene: C.Scene;
  private readonly camera: C.Camera;
  mode: 'nation' | 'site' = 'nation';
  layers: Layers3D = { ...INITIAL_LAYERS };
  autoRotate = false;
  google = false;
  private tileset: C.Cesium3DTileset | null = null;
  private fail = 0;
  private failShown = false;
  private locked: { id: string; ty: number } | null = null;
  private flying = false;
  private ptrDown = false;
  private readonly geo = new Map<string, SiteGeo>();
  private readonly draws = new Map<string, SiteDraw>();
  private readonly nat: {
    lines: C.PolylineCollection | null;
    pins: C.PointPrimitiveCollection;
    pinMap: Map<string, C.PointPrimitive>;
    mark: C.PointPrimitive;
  };
  private sp: {
    id: string | null;
    coll: C.PrimitiveCollection | null;
    terrain: C.Primitive | null;
    ctx: C.Primitive | null;
    design: C.PolylineCollection | null;
    solids: C.Primitive | null;
    trans: C.Primitive | null;
    edges: C.Primitive | null;
    dyn: BoxPool | null;
    dynT: BoxPool | null;
    eq: Equip | null;
  } = {
    id: null,
    coll: null,
    terrain: null,
    ctx: null,
    design: null,
    solids: null,
    trans: null,
    edges: null,
    dyn: null,
    dynT: null,
    eq: null,
  };
  mapDirty = true;
  sceneDirty = true;
  terrainDirty = true;
  private calRunning = false;
  private readonly labelEls = new Map<string, HTMLDivElement>();
  private readonly markEl: HTMLDivElement;
  private readonly lblCache = new Map<string, string>();
  private readonly onDispose: (() => void)[] = [];

  constructor(
    container: HTMLElement,
    private readonly labelsEl: HTMLElement,
    private readonly scaleEl: HTMLElement,
    private readonly needleEl: SVGElement,
    private readonly store: Store,
    private readonly map: MapLines,
    private readonly hooks: SceneHooks,
  ) {
    const globe = new C.Globe(C.Ellipsoid.WGS84);
    globe.baseColor = C.Color.fromCssColorString('#9fb58a');
    globe.showGroundAtmosphere = false;
    globe.enableLighting = false;
    const v = new C.Viewer(container, {
      baseLayer: false,
      globe,
      animation: false,
      timeline: false,
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      scene3DOnly: true,
      msaaSamples: 4,
      requestRenderMode: false,
      shouldAnimate: false,
    });
    this.viewer = v;
    this.scene = v.scene;
    this.camera = v.camera;
    // 日本の正午（空の明るさを固定）
    v.clock.currentTime = C.JulianDate.fromIso8601('2026-10-06T03:00:00Z');
    v.clock.shouldAnimate = false;
    v.scene.highDynamicRange = false;
    v.scene.postProcessStages.fxaa.enabled = true;
    v.scene.fog.enabled = true;
    const ssc = v.scene.screenSpaceCameraController;
    ssc.minimumZoomDistance = 6;
    ssc.maximumZoomDistance = 2.6e7;
    for (const s of store.views)
      this.geo.set(s.site.id, { geo: GEOID0[s.site.pref] ?? 36, geoCal: false });

    // ピンとラベル
    const pins = this.scene.primitives.add(
      new C.PointPrimitiveCollection(),
    ) as C.PointPrimitiveCollection;
    const pinMap = new Map<string, C.PointPrimitive>();
    for (const v2 of store.views) {
      const s = v2.site;
      pinMap.set(
        s.id,
        pins.add({
          position: this.pinPos(s),
          pixelSize: 11,
          outlineWidth: 3,
          outlineColor: C.Color.fromCssColorString(safeColor(s.color)),
          color: C.Color.WHITE,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          id: s.id,
        }),
      );
      const d = document.createElement('div');
      d.className = 'lbl';
      d.addEventListener('click', () => store.select(s.id));
      labelsEl.appendChild(d);
      this.labelEls.set(s.id, d);
    }
    const mark = pins.add({
      position: C.Cartesian3.ZERO,
      pixelSize: 12,
      color: C.Color.WHITE,
      outlineColor: C.Color.fromCssColorString('#0f172a'),
      outlineWidth: 3,
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
      show: false,
    });
    this.nat = { lines: null, pins, pinMap, mark };
    this.markEl = document.createElement('div');
    this.markEl.className = 'lbl mark';
    labelsEl.appendChild(this.markEl);
    this.buildNation();
    this.camera.setView({
      destination: C.Cartesian3.fromDegrees(HOME.lon, HOME.lat - 18, 1.6e7),
      orientation: { heading: 0, pitch: -1.5, roll: 0 },
    });
    this.flyHome(2.2);

    // クリック: ピン → 現場へ／地面 → その都道府県で絞り込む
    const hd = new C.ScreenSpaceEventHandler(this.scene.canvas);
    hd.setInputAction((e: { position: C.Cartesian2 }) => {
      if (this.mode !== 'nation') return;
      const pk = this.scene.pick(e.position) as { id?: unknown } | undefined;
      if (pk && typeof pk.id === 'string' && store.view(pk.id)) {
        store.select(pk.id);
        return;
      }
      const c = this.camera.pickEllipsoid(e.position, C.Ellipsoid.WGS84);
      if (!c) return;
      const cg = C.Cartographic.fromCartesian(c);
      const p = pointInPref(
        map.prefs,
        C.Math.toDegrees(cg.longitude),
        C.Math.toDegrees(cg.latitude),
      );
      hooks.pickPref(p && p.name !== store.state.pref ? p.name : '');
    }, C.ScreenSpaceEventType.LEFT_CLICK);
    let lastPick = 0;
    hd.setInputAction((e: { endPosition: C.Cartesian2 }) => {
      if (this.mode !== 'nation') {
        this.scene.canvas.style.cursor = '';
        return;
      }
      const tn = performance.now();
      if (tn - lastPick < 120) return;
      lastPick = tn;
      const pk = this.scene.pick(e.endPosition) as { id?: unknown } | undefined;
      this.scene.canvas.style.cursor =
        pk && typeof pk.id === 'string' && store.view(pk.id) ? 'pointer' : '';
    }, C.ScreenSpaceEventType.MOUSE_MOVE);
    this.onDispose.push(() => hd.destroy());
    const down = () => (this.ptrDown = true);
    const up = () => (this.ptrDown = false);
    this.scene.canvas.addEventListener('pointerdown', down);
    window.addEventListener('pointerup', up);
    this.onDispose.push(() => window.removeEventListener('pointerup', up));

    let lastTerr = 0;
    let lastScene = 0;
    let lastT = performance.now();
    this.scene.preUpdate.addEventListener(() => {
      const now = performance.now();
      const dt = Math.min(0.05, (now - lastT) / 1000);
      lastT = now;
      if (this.mapDirty) this.buildNation();
      if (this.tileset) this.tileset.show = this.useG();
      this.scene.globe.show = !this.useG();
      this.updatePins(now);
      const v3 = store.selected;
      if (this.mode === 'site' && v3 && this.sp.id === v3.site.id) {
        if (this.sp.ctx) this.sp.ctx.show = !this.useG();
        if (this.sp.design) this.sp.design.show = this.layers.design;
        if (this.terrainDirty && now - lastTerr > 110) {
          lastTerr = now;
          this.updateTerrain(v3);
          this.terrainDirty = false;
        }
        if (this.sceneDirty && now - lastScene > 110) {
          lastScene = now;
          this.buildSiteScene(v3);
        }
        const d = this.draw(v3.site);
        const dyn = dynamicItems(d, v3.state, this.sp.eq, this.layers, now / 1000);
        const fr = this.siteFrame(v3.site);
        this.sp.dyn!.begin();
        this.sp.dynT!.begin();
        for (const it of dyn.sol) this.sp.dyn!.add(fr, it.m, it.c);
        for (const it of dyn.tr) this.sp.dynT!.add(fr, it.m, it.c);
        this.sp.dyn!.end();
        this.sp.dynT!.end();
        if (this.autoRotate && this.locked && !this.flying && !this.ptrDown)
          this.camera.rotateRight(dt * 0.12);
      }
    });
    this.scene.postRender.addEventListener(() => {
      this.updateLabels();
      this.updateScale();
      this.needleEl.style.transform = `rotate(${-this.camera.heading}rad)`;
    });
  }

  dispose(): void {
    for (const f of this.onDispose) f();
    this.viewer.destroy();
  }

  useG(): boolean {
    return this.google && this.layers.g3d;
  }

  // ---- 座標の変換（ローカル: x=東, y=上, z=南、原点は施工範囲の中心、高さ 0 は T.P. yRef） ----
  private sg(s: Site): SiteGeo {
    return this.geo.get(s.id)!;
  }
  geoOf(s: Site): { geo: number; geoCal: boolean } {
    return this.sg(s);
  }
  private siteFrame(s: Site): C.Matrix4 {
    const g = this.sg(s);
    if (!g.fr || g.frGeo !== g.geo) {
      g.enu = C.Transforms.eastNorthUpToFixedFrame(
        C.Cartesian3.fromDegrees(s.lon, s.lat, s.yRef + g.geo),
      );
      g.fr = C.Matrix4.multiply(g.enu, P_YUP, new C.Matrix4());
      g.frGeo = g.geo;
      g.pin = undefined;
    }
    return g.fr;
  }
  private enu(s: Site): C.Matrix4 {
    this.siteFrame(s);
    return this.sg(s).enu!;
  }
  private enuAt(s: Site, ty: number): C.Matrix4 {
    return C.Transforms.eastNorthUpToFixedFrame(
      C.Cartesian3.fromDegrees(s.lon, s.lat, s.yRef + this.sg(s).geo + ty),
    );
  }
  private L2W(s: Site, x: number, y: number, z: number): C.Cartesian3 {
    return C.Matrix4.multiplyByPoint(
      this.siteFrame(s),
      new C.Cartesian3(x, y, z),
      new C.Cartesian3(),
    );
  }
  private pinPos(s: Site): C.Cartesian3 {
    this.siteFrame(s);
    const g = this.sg(s);
    if (!g.pin) g.pin = C.Cartesian3.fromDegrees(s.lon, s.lat, s.yRef + g.geo + 2);
    return g.pin;
  }
  private draw(s: Site): SiteDraw {
    let d = this.draws.get(s.id);
    if (!d || d.site !== s) {
      d = createSiteDraw(s);
      this.draws.set(s.id, d);
    }
    return d;
  }

  // ---- カメラ ----
  private unlock(): void {
    this.camera.lookAtTransform(C.Matrix4.IDENTITY);
    this.locked = null;
  }
  private flyAround(
    center: C.Cartesian3,
    heading: number,
    pitch: number,
    range: number,
    dur: number,
    done?: () => void,
  ): void {
    this.unlock();
    this.flying = true;
    this.camera.flyToBoundingSphere(new C.BoundingSphere(center, 1), {
      offset: new C.HeadingPitchRange(heading, pitch, range),
      duration: dur,
      complete: () => {
        this.flying = false;
        done?.();
      },
      cancel: () => {
        this.flying = false;
      },
    });
  }
  flyHome(dur = 2): void {
    this.flyAround(
      C.Cartesian3.fromDegrees(HOME.lon, HOME.lat, 0),
      HOME.heading,
      HOME.pitch,
      HOME.range,
      dur,
    );
  }
  flyPref(name: string, dur = 1.8): void {
    const p = this.map.prefs.find((x) => x.name === name);
    if (!p) return this.flyHome(1.6);
    const b = p.main;
    const lat = (b[1] + b[3]) / 2;
    const dx = (b[2] - b[0]) * Math.cos((lat * Math.PI) / 180) * 111e3;
    const dy = (b[3] - b[1]) * 111e3;
    this.flyAround(
      C.Cartesian3.fromDegrees((b[0] + b[2]) / 2, lat, 0),
      0,
      -1.0,
      clamp(Math.max(dx, dy) * 1.7, 1.2e5, 2.4e6),
      dur,
    );
  }
  flyPoint(lon: number, lat: number): void {
    this.flyAround(C.Cartesian3.fromDegrees(lon, lat, 0), 0, -0.95, 5000, 2);
  }
  siteView(s: Site, over: Partial<SiteView3D> = {}): SiteView3D {
    const R = Math.max(s.area.w, s.area.d);
    const H = Math.max(10, siteTop(s));
    return {
      ty: s.FH - s.yRef + H * 0.25,
      yaw: 0.75,
      pitch: 0.55,
      dist: (R * 1.15 + H * 1.1 + 40) * 1.25,
      ...over,
    };
  }
  private lockSite(s: Site, ty: number, hpr: C.HeadingPitchRange): void {
    this.camera.lookAtTransform(this.enuAt(s, ty), hpr);
    this.locked = { id: s.id, ty };
  }
  flySite(s: Site, v: SiteView3D, dur = 1.4): void {
    const c = this.L2W(s, 0, v.ty, 0);
    const hpr = new C.HeadingPitchRange(-v.yaw, -v.pitch, v.dist);
    this.flyAround(c, hpr.heading, hpr.pitch, hpr.range, dur, () => {
      if (this.store.state.sel === s.id && this.mode === 'site') {
        this.lockSite(s, v.ty, hpr);
        void this.calibrateGeoid(s);
      }
    });
  }
  private currentHPR(): C.HeadingPitchRange | null {
    if (!this.locked) return null;
    const p = this.camera.position;
    const r = C.Cartesian3.magnitude(p);
    if (!(r > 0)) return null;
    return new C.HeadingPitchRange(Math.atan2(-p.x, -p.y), -Math.asin(clamp(p.z / r, -1, 1)), r);
  }
  private currentYaw(): number {
    return -this.camera.heading;
  }

  /** 現場を選んだとき・外したとき（サンプルの enterSite・enterNation）。 */
  enterSite(v: SiteView, viaNation: boolean): void {
    this.mode = 'site';
    this.ensureSite(v);
    this.applyClip();
    this.setSiteLight(v.site);
    this.sceneDirty = this.terrainDirty = true;
    this.flySite(v.site, this.siteView(v.site), viaNation ? 2.8 : 2.0);
  }
  enterNation(prev: SiteView | undefined): void {
    this.mode = 'nation';
    this.clearSite();
    this.applyClip();
    this.sceneDirty = true;
    const pref = this.store.state.pref;
    if (pref) this.flyPref(pref, 2.2);
    else if (prev) this.flyAround(this.pinPos(prev.site), 0, -1.1, 1.6e6, 2.4);
    else this.flyHome();
  }

  /** 視点ボタン（サンプルの applyView）。 */
  applyView(view: string): void {
    const v = this.store.selected;
    if (this.mode === 'site' && v) {
      const s = v.site;
      const t = this.siteView(s, { yaw: this.currentYaw() });
      if (view === 'site') this.flySite(s, this.siteView(s));
      else if (view === 'bird') this.flySite(s, { ...t, pitch: 0.62 }, 1);
      else if (view === 'top') this.flySite(s, { ...t, pitch: 1.53, dist: t.dist * 1.05 }, 1);
      else if (view === 'eye') this.flySite(s, { ...t, pitch: 0.1, dist: t.dist * 0.85 }, 1);
      else if (view === 'goTop') this.flySite(s, this.siteView(s, { pitch: 1.53, yaw: 0, ty: 0 }));
      return;
    }
    if (view === 'all') return this.flyHome();
    const cv = this.scene.canvas;
    const ray = this.camera.getPickRay(new C.Cartesian2(cv.clientWidth / 2, cv.clientHeight / 2));
    const hit = ray ? C.IntersectionTests.rayEllipsoid(ray, C.Ellipsoid.WGS84) : undefined;
    const tgt =
      hit && ray
        ? C.Ray.getPoint(ray, hit.start)
        : C.Cartesian3.fromRadians(
            this.camera.positionCartographic.longitude,
            this.camera.positionCartographic.latitude,
            0,
          );
    const range = C.Cartesian3.distance(this.camera.positionWC, tgt);
    this.flyAround(tgt, this.camera.heading, view === 'top' ? -1.5 : -0.8, range, 1);
  }

  /** コンパス（北を上に戻す）。 */
  northUp(): void {
    const v = this.store.selected;
    if (this.locked && v) {
      const h = this.currentHPR();
      if (!h) return;
      const ty = this.locked.ty;
      this.flyAround(this.L2W(v.site, 0, ty, 0), 0, h.pitch, h.range, 0.7, () =>
        this.lockSite(v.site, ty, new C.HeadingPitchRange(0, h.pitch, h.range)),
      );
    } else {
      this.camera.flyTo({
        destination: this.camera.positionWC.clone(),
        orientation: { heading: 0, pitch: this.camera.pitch, roll: 0 },
        duration: 0.7,
      });
    }
  }

  setLayer(k: string, on: boolean): void {
    if (!(k in this.layers)) return;
    this.layers[k as keyof Layers3D] = on;
    this.sceneDirty = this.terrainDirty = true;
    if (k === 'roads' || k === 'g3d') this.mapDirty = true;
    if (k === 'g3d') {
      this.hooks.onGoogleChange();
      const v = this.store.selected;
      if (on && v) void this.calibrateGeoid(v.site);
    }
  }

  // ---- 全国表示 ----
  private lineH(): number {
    return this.useG() ? 4500 : 400;
  }
  private buildNation(): void {
    if (this.nat.lines) this.scene.primitives.remove(this.nat.lines);
    const pc = new C.PolylineCollection();
    const h = this.lineH();
    // Material は Polyline 1 本ごとに作る（colorMaterial の【不変条件】を参照）。共有すると
    // PolylineCollection の destroy で二重破棄になり、Cesium が描画を恒久停止する。
    for (const s of nationLineSpecs(this.map, this.store.state.pref, this.layers.roads, h))
      pc.add({
        positions: degreesArrayHeights(s.positions, s.height),
        width: s.width,
        material: colorMaterial(nationLineColor(s.kind)),
      });
    this.nat.lines = this.scene.primitives.add(pc) as C.PolylineCollection;
    this.mapDirty = false;
  }
  private updatePins(now: number): void {
    const nation = this.mode === 'nation';
    const height = this.camera.positionCartographic.height;
    const STC = {
      ok: C.Color.fromCssColorString('#22c55e'),
      warn: C.Color.fromCssColorString('#f59e0b'),
      late: C.Color.fromCssColorString('#ef4444'),
    };
    for (const v of this.store.views) {
      const s = v.site;
      const p = this.nat.pinMap.get(s.id)!;
      const d = v.cur.delay;
      const sel = this.store.state.sel === s.id;
      p.show = nation && this.store.matches(v);
      p.position = this.pinPos(s);
      p.color = d > 14 ? STC.late : d > 0 ? STC.warn : STC.ok;
      p.pixelSize = sel ? 15 + 3 * Math.sin(now / 200) : 11;
    }
    const m = this.store.state.mark;
    this.nat.mark.show = nation && !!m && !this.useG();
    if (m) this.nat.mark.position = C.Cartesian3.fromDegrees(m.lon, m.lat, this.lineH() * 0.2);
    if (this.nat.lines) this.nat.lines.show = nation && (!this.useG() || height > 9e4);
  }

  // ---- ラベル（HTML） ----
  private project(pos: C.Cartesian3): { x: number; y: number; w: number } | null {
    const cam = this.camera;
    const v = C.Cartesian3.subtract(pos, cam.positionWC, new C.Cartesian3());
    const dist = C.Cartesian3.magnitude(v);
    if (C.Cartesian3.dot(v, cam.directionWC) <= 0) return null;
    // 全国表示では、地球の裏側の点を隠す。サンプルは内部クラス EllipsoidalOccluder を使うが、
    // npm 版は公開していないので、地球（半径は短半径）を遮へい物とする公開 API の Occluder で同じ判定をする
    if (this.mode === 'nation') {
      const occ = new C.Occluder(
        new C.BoundingSphere(C.Cartesian3.ZERO, C.Ellipsoid.WGS84.minimumRadius),
        cam.positionWC,
      );
      if (!occ.isPointVisible(pos)) return null;
    }
    const w = C.SceneTransforms.worldToWindowCoordinates(this.scene, pos);
    return w ? { x: w.x, y: w.y, w: dist } : null;
  }
  private updateLabels(): void {
    const cv = this.scene.canvas;
    const W = cv.clientWidth;
    const H = cv.clientHeight;
    const rects: number[][] = [];
    const nation = this.mode === 'nation';
    const camH = this.camera.positionCartographic.height;
    const sel = this.store.state.sel;
    const T = this.store.state.T;
    const order = [...this.store.views].sort(
      (a, b) => Number(b.site.id === sel) - Number(a.site.id === sel),
    );
    for (const v of order) {
      const s = v.site;
      const el = this.labelEls.get(s.id)!;
      let pr: ReturnType<CesiumScene['project']> = null;
      if (nation) pr = this.project(this.pinPos(s));
      else if (s.id === sel) pr = this.project(this.L2W(s, 0, siteTop(s) + 6, 0));
      if (
        !this.layers.labels ||
        !this.store.matches(v) ||
        !pr ||
        pr.x < -120 ||
        pr.x > W + 120 ||
        pr.y < -60 ||
        pr.y > H + 60
      ) {
        el.style.display = 'none';
        continue;
      }
      const p = Math.round(v.state.overall * 100);
      const pred = T > B;
      const compact = nation && camH > 6e5;
      const earthing = v.state.e > 0 && v.state.e < 1;
      const key = `${p}|${sel === s.id}|${pred}|${compact}|${earthing}`;
      const color = safeColor(s.color);
      if (this.lblCache.get(s.id) !== key) {
        this.lblCache.set(s.id, key);
        el.className = `lbl${sel === s.id ? ' sel' : ''}${compact ? ' mini2' : ''}`;
        el.innerHTML = compact
          ? `<div class="t"><span class="dot" style="background:${color}"></span>${esc(s.pref.replace(/(都|府|県)$/, ''))} ${p}%</div>`
          : `<div class="t"><span class="dot" style="background:${color}"></span>${esc(s.id)}${pred ? ' <span class="pr">予測</span>' : ''}</div><div class="s">${esc(s.name.length > 14 ? `${s.name.slice(0, 14)}…` : s.name)}</div><div class="s"><span class="mini"><i style="width:${p}%;background:${color}"></i></span>${p}%${earthing ? '・土工中' : ''}</div>`;
      }
      el.style.display = '';
      const bw = el.offsetWidth || 80;
      const bh = el.offsetHeight || 24;
      const oy = nation ? 10 : 0;
      const r = [pr.x - bw / 2, pr.y - bh - 8 - oy, pr.x + bw / 2, pr.y - 8 - oy];
      if (
        nation &&
        rects.some((q) => !(r[2]! < q[0]! || r[0]! > q[2]! || r[3]! < q[1]! || r[1]! > q[3]!))
      ) {
        el.style.display = 'none';
        continue;
      }
      rects.push(r);
      el.style.transform = `translate(${pr.x}px,${pr.y - oy}px) translate(-50%,-100%) translateY(-6px)`;
      el.style.zIndex = String(sel === s.id ? 100000 : Math.round(50000 - pr.w / 1000));
    }
    const m = this.store.state.mark;
    if (nation && m && !this.useG()) {
      const pr = this.project(C.Cartesian3.fromDegrees(m.lon, m.lat, this.lineH() * 0.2));
      if (pr) {
        this.markEl.style.display = '';
        this.markEl.textContent = `📍 ${m.label}`;
        this.markEl.style.transform = `translate(${pr.x}px,${pr.y - 10}px) translate(-50%,-100%) translateY(-6px)`;
      } else this.markEl.style.display = 'none';
    } else this.markEl.style.display = 'none';
  }
  private updateScale(): void {
    const cv = this.scene.canvas;
    const Hc = cv.clientHeight;
    if (!Hc) return;
    const cam = this.camera;
    let dist: number;
    const v = this.store.selected;
    if (this.mode === 'site' && v)
      dist = C.Cartesian3.distance(cam.positionWC, this.L2W(v.site, 0, 0, 0));
    else {
      const ray = cam.getPickRay(new C.Cartesian2(cv.clientWidth / 2, Hc / 2));
      const hit = ray ? C.IntersectionTests.rayEllipsoid(ray, C.Ellipsoid.WGS84) : undefined;
      dist = hit ? hit.start : cam.positionCartographic.height;
    }
    const fovy = (cam.frustum as C.PerspectiveFrustum).fovy || 0.8;
    const mPerPx = (2 * dist * Math.tan(fovy / 2)) / Hc;
    const target = mPerPx * 90;
    const pow = Math.pow(10, Math.floor(Math.log10(target)));
    const nice =
      [1, 2, 5, 10]
        .map((k) => k * pow)
        .filter((x) => x <= target * 1.4)
        .pop() ?? pow;
    const px = nice / mPerPx;
    this.scaleEl.innerHTML = `${nice >= 1000 ? `${(nice / 1000).toLocaleString('ja-JP')} km` : `${nice} m`}<i style="width:${px}px;margin-left:auto"></i>`;
  }

  // ---- 現場表示 ----
  private clearSite(): void {
    if (this.sp.coll) this.scene.primitives.remove(this.sp.coll);
    this.sp = {
      id: null,
      coll: null,
      terrain: null,
      ctx: null,
      design: null,
      solids: null,
      trans: null,
      edges: null,
      dyn: null,
      dynT: null,
      eq: null,
    };
  }
  private ensureSite(v: SiteView): void {
    const s = v.site;
    if (this.sp.id === s.id && this.sp.coll) return;
    this.clearSite();
    this.siteFrame(s);
    const coll = this.scene.primitives.add(new C.PrimitiveCollection()) as C.PrimitiveCollection;
    this.sp.id = s.id;
    this.sp.coll = coll;
    this.sp.dyn = new BoxPool(coll, false);
    this.sp.dynT = new BoxPool(coll, true);
    const d = this.draw(s);
    const m = contextMesh(d);
    this.sp.ctx = coll.add(
      meshPrimitive(
        this.enu(s),
        gridGeometry(m.n, m.n, m.X, m.Z, m.H, m.COL, s.yRef, m.skirtBase, hex('#8a7356')),
        true,
        d.contI,
      ),
    ) as C.Primitive;
    this.buildDesignLines(s);
  }
  /** 計画地盤高・根切り深さが変わったとき（計画面の網目を作り直す）。 */
  designChanged(): void {
    const v = this.store.selected;
    if (v && this.sp.id === v.site.id) this.buildDesignLines(v.site);
    this.sceneDirty = this.terrainDirty = true;
  }
  private buildDesignLines(s: Site): void {
    const coll = this.sp.coll!;
    if (this.sp.design) {
      coll.remove(this.sp.design);
      this.sp.design = null;
    }
    const pc = new C.PolylineCollection({ modelMatrix: C.Matrix4.clone(this.siteFrame(s)) });
    for (const line of designLines(s))
      pc.add({
        positions: line.map(([x, y, z]) => new C.Cartesian3(x, y, z)),
        width: 1.5,
        // Material は Polyline 1 本ごとに作る（colorMaterial の【不変条件】を参照）。
        material: colorMaterial(new C.Color(0.1, 0.45, 0.95, 0.7)),
      });
    pc.show = this.layers.design;
    this.sp.design = coll.add(pc) as C.PolylineCollection;
  }
  private setSiteLight(s: Site): void {
    const d = C.Matrix4.multiplyByPointAsVector(
      this.siteFrame(s),
      new C.Cartesian3(-0.45, -1, -0.3),
      new C.Cartesian3(),
    );
    this.scene.light = new C.DirectionalLight({
      direction: C.Cartesian3.normalize(d, d),
      intensity: 2,
    });
  }
  private updateTerrain(v: SiteView): void {
    const s = v.site;
    const d = this.draw(s);
    updateGround(d, v.state, this.layers.heat);
    const g = s.g;
    const geom = gridGeometry(
      g.nx,
      g.nz,
      g.LX,
      g.LZ,
      d.H,
      d.col,
      s.yRef,
      g.minH - 14,
      hex('#8a7356'),
    );
    const prim = this.sp.coll!.add(
      meshPrimitive(this.enu(s), geom, !this.layers.heat, d.contI || 2),
    ) as C.Primitive;
    if (this.sp.terrain) this.sp.coll!.remove(this.sp.terrain);
    this.sp.terrain = prim;
    this.sp.eq = computeEquip(d, v.state, v.plan);
  }
  private buildSiteScene(v: SiteView): void {
    const s = v.site;
    const S = sceneItems(this.draw(s), v.state);
    const fr = this.siteFrame(s);
    const coll = this.sp.coll!;
    const old = [this.sp.solids, this.sp.trans, this.sp.edges];
    const add = (p: C.Primitive | null) => (p ? (coll.add(p) as C.Primitive) : null);
    this.sp.solids = add(boxesPrimitive(fr, S.solids, 'solid'));
    this.sp.trans = add(boxesPrimitive(fr, [...S.ghosts, ...S.trans], 'trans'));
    this.sp.edges = add(boxesPrimitive(fr, [...S.edges, ...S.gedges], 'edge'));
    for (const p of old) if (p) coll.remove(p);
    this.sceneDirty = false;
  }

  // ---- Google 実写 3D ----
  async connectGoogle(key: string): Promise<boolean> {
    if (!key) return false;
    this.hooks.setStatus('loading');
    try {
      const ts = await C.createGooglePhotorealistic3DTileset(
        { key, onlyUsingWithGoogleGeocoder: true },
        {
          cacheBytes: 512 * 1024 * 1024,
          maximumCacheOverflowBytes: 256 * 1024 * 1024,
          showCreditsOnScreen: true,
        },
      );
      if (this.tileset) this.scene.primitives.remove(this.tileset);
      this.tileset = this.scene.primitives.add(ts) as C.Cesium3DTileset;
      this.google = true;
      this.fail = 0;
      this.failShown = false;
      ts.tileFailed.addEventListener(() => {
        if (++this.fail >= 6 && !this.failShown) {
          this.failShown = true;
          this.hooks.toast(
            '一部の Google 3D タイルを取得できません。<br>1日の上限（クォータ）・請求先・キーのリファラ制限・ネットワークを確認してください。',
            10000,
          );
        }
      });
      this.applyClip();
      this.mapDirty = true;
      this.hooks.setStatus('google');
      this.hooks.onGoogleChange();
      const v = this.store.selected;
      if (v) {
        this.sg(v.site).geoCal = false;
        void this.calibrateGeoid(v.site);
      }
      return true;
    } catch (e) {
      this.google = false;
      this.hooks.setStatus('error', 'Google 3D に接続できませんでした（詳細は APIキー画面）');
      const d = await diagnoseGoogle(key, e);
      this.hooks.toast(
        `Google 3D に接続できませんでした：${esc(d.title)}<br>簡易地形モードで表示しています。原因と対処は APIキー画面に表示しました。`,
        12000,
      );
      this.hooks.onConnectFailed(d, key);
      return false;
    }
  }
  disconnectGoogle(): void {
    if (this.tileset) {
      this.scene.primitives.remove(this.tileset);
      this.tileset = null;
    }
    this.google = false;
    this.hooks.setStatus('simple');
    this.mapDirty = true;
    this.hooks.onGoogleChange();
  }
  /** 施工範囲（外側 0.6m を含む）だけ Google の 3D を切り抜く（元仕様 §8.4）。 */
  private applyClip(): void {
    if (!this.tileset) return;
    const v = this.store.selected;
    if (this.mode !== 'site' || !v) {
      this.tileset.clippingPolygons = undefined as unknown as C.ClippingPolygonCollection;
      return;
    }
    const s = v.site;
    const hw = s.area.w / 2 + 0.6;
    const hd = s.area.d / 2 + 0.6;
    const positions = [
      [-hw, -hd],
      [hw, -hd],
      [hw, hd],
      [-hw, hd],
    ].map(([x, z]) => this.L2W(s, x!, 0, z!));
    this.tileset.clippingPolygons = new C.ClippingPolygonCollection({
      polygons: [new C.ClippingPolygon({ positions })],
    });
  }
  /** 現場の周りの Google の地面と標高を比べ、自社の地形の高さを合わせる（元仕様 §8.5）。 */
  private async calibrateGeoid(s: Site): Promise<void> {
    const g = this.sg(s);
    if (!this.useG() || g.geoCal || this.calRunning || !this.scene.sampleHeightSupported) return;
    this.calRunning = true;
    try {
      const pts: C.Cartographic[] = [];
      const ref: number[] = [];
      for (const k of [1.2, 1.5, 1.9]) {
        for (let a = 0; a < 16; a++) {
          const ang = ((a + k * 3) / 16) * Math.PI * 2;
          const x = Math.cos(ang) * ((s.area.w / 2) * k + 12);
          const z = Math.sin(ang) * ((s.area.d / 2) * k + 12);
          if (Math.abs(x) > 470 || Math.abs(z) > 470) continue;
          pts.push(C.Cartographic.fromCartesian(this.L2W(s, x, 0, z)));
          ref.push(sampleContext(s.ctx, x, z));
        }
      }
      const exclude: object[] = [];
      if (this.sp.coll)
        for (let i = 0; i < this.sp.coll.length; i++) exclude.push(this.sp.coll.get(i) as object);
      const res = await this.scene.sampleHeightMostDetailed(pts, exclude);
      const diffs = res
        .map((p, i) => (p && Number.isFinite(p.height) ? p.height - ref[i]! : NaN))
        .filter(Number.isFinite)
        .sort((a, b) => a - b);
      if (diffs.length >= 8) {
        const geo = diffs[Math.floor(diffs.length * 0.2)]!;
        if (geo > 15 && geo < 60) {
          const changed = Math.abs(geo - g.geo) > 0.25;
          g.geo = Math.round(geo * 10) / 10;
          g.geoCal = true;
          const v = this.store.selected;
          if (changed && v && v.site.id === s.id) {
            const keep = this.currentHPR();
            this.clearSite();
            this.ensureSite(v);
            this.applyClip();
            this.setSiteLight(s);
            this.terrainDirty = this.sceneDirty = true;
            if (this.locked && keep) this.lockSite(s, this.locked.ty, keep);
          }
          this.hooks.onGoogleChange();
        }
      }
    } catch (e) {
      console.warn(e);
    } finally {
      this.calRunning = false;
    }
  }
}

/** 経度・緯度がどの都道府県の中か（サンプルの pointInPref）。 */
export function pointInPref(
  prefs: readonly PrefShape[],
  lon: number,
  lat: number,
): PrefShape | null {
  for (const p of prefs) {
    for (const r of p.rings) {
      let inside = false;
      for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
        const [xi, yi] = r[i]!;
        const [xj, yj] = r[j]!;
        if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)
          inside = !inside;
      }
      if (inside) return p;
    }
  }
  return null;
}

/** Google に接続できなかった原因を調べ、日本語の対処を返す（元仕様 §8.6、サンプルの diagnoseGoogle）。 */
export async function diagnoseGoogle(
  key: string,
  err: unknown,
  fetcher: typeof fetch = fetch,
): Promise<Diagnosis> {
  const out: Diagnosis = { title: '', steps: [], raw: '' };
  if (location.protocol === 'file:') {
    out.title = 'ファイルを直接開いています';
    out.steps = [
      'このファイルは Web サーバに置いて https:// で開いてください（GitHub Pages、社内Webサーバなど）。',
      '手元で試すだけなら、ファイルのあるフォルダで <code>python -m http.server 8000</code> を実行し、<code>http://localhost:8000/ファイル名.html</code> を開きます。その場合、キーのリファラ制限に <code>http://localhost:8000/*</code> を追加してください。',
    ];
  }
  let r: Response;
  try {
    r = await fetcher(
      `https://tile.googleapis.com/v1/3dtiles/root.json?key=${encodeURIComponent(key)}`,
      { cache: 'no-store' },
    );
  } catch (e2) {
    if (!out.title) out.title = 'Google のサーバ（tile.googleapis.com）に接続できません';
    out.steps.push(
      'この画面をどこで開いているか確認してください。Claude アプリ内のプレビューやメール添付のプレビューでは外部への通信が止められます。ブラウザ（Chrome / Edge）で URL を開いてください。',
      // サンプルは cdn.jsdelivr.net も挙げるが、本基盤は CesiumJS を同梱して CDN を使わない（ADR-0001）
      '社内ネットワークのプロキシ・UTM（FortiGate の Webフィルタ／SSLインスペクション等）で <code>tile.googleapis.com</code> が許可されているか確認してください。',
      'スマホのテザリングなど社外回線で同じ URL を開いて表示できれば、社内ネットワーク側の制限です。',
    );
    out.raw = String((err as Error | undefined)?.message ?? err ?? e2);
    return out;
  }
  let msg = '';
  let reason = '';
  try {
    const j = (await r.clone().json()) as {
      error?: { message?: string; status?: string; details?: unknown };
    };
    msg = j.error?.message ?? '';
    reason =
      /"reason":"([A-Z_]+)"/.exec(JSON.stringify(j.error?.details ?? ''))?.[1] ??
      j.error?.status ??
      '';
  } catch {
    // 本文が JSON でないときは、状態コードだけで判断する
  }
  // キーそのものは表示しない（FR-G3D-09）
  out.raw = `HTTP ${r.status}${reason ? ` ${reason}` : ''}：${msg}`.slice(0, 400);
  if (r.ok) {
    out.title ||= 'キーは有効ですが、3D の初期化で失敗しました';
    out.steps.push(
      'ページを再読み込みしてもう一度試してください。続く場合は下の詳細を担当者に伝えてください。',
    );
    out.raw += ` ／ ${String((err as Error | undefined)?.message ?? err).slice(0, 300)}`;
    return out;
  }
  const m = `${msg} ${reason}`.toLowerCase();
  if (r.status === 400 || m.includes('api_key_invalid') || m.includes('not valid')) {
    out.title = 'APIキーが無効です';
    out.steps.push(
      'キーをコピーし直してください（前後の空白・改行に注意）。作成直後は有効になるまで数分かかることがあります。',
    );
  } else if (m.includes('referer') || m.includes('referrer')) {
    out.title = 'HTTPリファラ制限でこのページが許可されていません';
    out.steps.push(
      `Cloud Console → APIとサービス → 認証情報 → このキー → 「ウェブサイトの制限」に <code>${esc(location.origin)}/*</code> を追加してください（反映まで最大5分）。ブラウザは Google へサイト名（<code>${esc(location.origin)}/</code>）だけを送るので、フォルダ名まで入れた登録では一致しません。`,
    );
  } else if (
    m.includes('has not been used') ||
    m.includes('is disabled') ||
    m.includes('service_disabled') ||
    m.includes('not been enabled')
  ) {
    out.title = 'Map Tiles API が有効になっていません';
    out.steps.push(
      'Cloud Console → APIとサービス → ライブラリ で「Map Tiles API」を検索し「有効にする」を押してください（キーを作ったのと同じプロジェクトで）。',
    );
  } else if (m.includes('billing')) {
    out.title = 'プロジェクトに請求先アカウントが設定されていません';
    out.steps.push(
      'Cloud Console → お支払い で、キーのプロジェクトに請求先アカウントをリンクしてください。あわせて予算アラートと1日の上限も設定してください。',
    );
  } else if (m.includes('api_key_service_blocked') || m.includes('blocked')) {
    out.title = 'キーの「API の制限」で Map Tiles API が許可されていません';
    out.steps.push(
      '認証情報 → このキー → 「API の制限」で <b>Map Tiles API</b> にチェックを入れて保存してください。',
    );
  } else if (r.status === 404) {
    out.title = 'キーは有効ですが、このキーのプロジェクトで 3D Tiles を使える状態になっていません';
    out.steps.push(
      'まず「お支払い」で、キーのプロジェクトに<b>請求先アカウントがリンクされているか</b>確認してください（いちばん多い原因です。無料枠だけで使う場合もリンクが必要）。',
      'Cloud Console 上部のプロジェクト選択が、キーを作ったプロジェクトになっているか確認してください。',
      'そのプロジェクトで「APIとサービス → 有効な API」に <b>Map Tiles API</b> があるか確認し、なければ有効にしてください。',
      '直らない場合は、Google Maps Platform の「キーと認証情報」から新しいキーを作り直して試してください。',
    );
  } else if (r.status === 429 || m.includes('quota') || m.includes('rate')) {
    out.title = '利用上限（クォータ）に達しています';
    out.steps.push(
      'Cloud Console → Map Tiles API → 割り当て で上限を確認してください。日付が変わると回復します。',
    );
  } else {
    out.title ||= `Google が接続を拒否しました（HTTP ${r.status}）`;
    out.steps.push(
      '下の詳細メッセージを確認してください。Map Tiles API の有効化・請求先・キーの制限の3点を見直すと多くの場合解決します。',
    );
  }
  return out;
}
